// @ts-check

import { isToolUIPart } from "ai"
import { getAgentToolDisplayLabel } from "./agentToolDisplayLabels"
import {
  STREAM_ACTIVITY_KIND_REASONING,
  STREAM_ACTIVITY_KIND_TOOL,
} from "./streamActivityReducer"

/** Texto cuando `set_memory` termina sin datos (p. ej. borrado de memorias). */
export const SET_MEMORY_EMPTY_OUTPUT_NOTICE =
  "Borrar todas las memorias almacenadas."

/**
 * @param {unknown} output
 * @returns {boolean}
 */
function isEmptyLikeSetMemoryOutput(output) {
  if (output === undefined || output === null) return true
  if (typeof output === "object" && output !== null && !Array.isArray(output)) {
    const o = /** @type {Record<string, unknown>} */ (output)
    if (Object.keys(o).length === 0) return true
    if (Array.isArray(o.memory) && o.memory.length === 0) return true
  }
  return false
}

/**
 * Input/preview de `set_memory`: vacío, `{}` o `{ memory: [] }` (p. ej. borrar memorias).
 *
 * @param {unknown} payload
 * @returns {boolean}
 */
export function isEmptyLikeSetMemoryPayload(payload) {
  if (payload === undefined || payload === null) return true
  if (typeof payload !== "object" || Array.isArray(payload)) return false
  const o = /** @type {Record<string, unknown>} */ (payload)
  if (Object.keys(o).length === 0) return true
  if (Array.isArray(o.memory) && o.memory.length === 0) return true
  return false
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @returns {string | undefined}
 */
export function toolIdFromPart(part) {
  if (part.type === "dynamic-tool" && "toolName" in part) {
    const n = part.toolName
    return typeof n === "string" && n.trim() ? n.trim() : undefined
  }
  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    const id = part.type.slice("tool-".length)
    return id.trim() || undefined
  }
  return undefined
}

/**
 * @param {import("ai").UIMessage} message
 * @returns {string}
 */
export function userMessageTextFromParts(message) {
  const parts = message.parts
  if (!parts?.length) return ""
  let s = ""
  for (const part of parts) {
    if (part.type === "text" && "text" in part) {
      s += part.text
    }
  }
  return s
}

/**
 * @param {string | undefined} state
 */
function toolActiveStates(state) {
  return (
    state === "input-streaming" ||
    state === "input-available" ||
    state === "approval-requested" ||
    state === "approval-responded"
  )
}

const ABORT_TOOL_ERROR = "Generación detenida"

/**
 * Cierra partes a medias tras detener el stream (texto/reasoning en done;
 * herramientas inacabadas como error para que el panel de actividad no quede en spinner).
 *
 * @param {import("ai").UIMessage["parts"][number][]} parts
 * @returns {import("ai").UIMessage["parts"][number][]}
 */
export function normalizeAssistantPartsAfterUserAbort(parts) {
  return parts.map((part) => {
    if (part.type === "text") {
      if (part.state !== "done") {
        return { ...part, state: /** @type {"done"} */ ("done") }
      }
      return part
    }
    if (part.type === "reasoning") {
      if (part.state !== "done") {
        return { ...part, state: /** @type {"done"} */ ("done") }
      }
      return part
    }
    if (part.type === "dynamic-tool") {
      const st = part.state
      if (typeof st === "string" && toolActiveStates(st)) {
        return /** @type {import("ai").UIMessage["parts"][number]} */ ({
          type: "dynamic-tool",
          toolName: part.toolName,
          toolCallId: part.toolCallId,
          title: part.title,
          providerExecuted: part.providerExecuted,
          state: "output-error",
          input: "input" in part ? part.input : undefined,
          errorText: ABORT_TOOL_ERROR,
        })
      }
      return part
    }
    if (typeof part.type === "string" && part.type.startsWith("tool-")) {
      const st = "state" in part ? part.state : undefined
      if (typeof st === "string" && toolActiveStates(st)) {
        return /** @type {import("ai").UIMessage["parts"][number]} */ ({
          ...part,
          state: "output-error",
          errorText: ABORT_TOOL_ERROR,
        })
      }
      return part
    }
    return part
  })
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @param {number} index
 * @returns {import("./estimationAgent.types").StreamActivitySegment}
 */
function reasoningPartToSegment(part, index) {
  if (part.type !== "reasoning") {
    throw new Error("expected reasoning part")
  }
  const text = part.text || ""
  const trimmed = text.trim()
  const label =
    trimmed ?
      text.length > 80 ?
        `${text.slice(0, 77)}...`
      : text
    : "Razonamiento"
  return {
    id: `reasoning-${index}`,
    kind: STREAM_ACTIVITY_KIND_REASONING,
    status: part.state === "streaming" ? "active" : "completed",
    label,
  }
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @param {number} index
 * @returns {import("./estimationAgent.types").StreamActivitySegment}
 */
function toolPartToSegment(part, index) {
  const id =
    "toolCallId" in part && typeof part.toolCallId === "string" ?
      part.toolCallId
    : `tool-${index}`
  const toolId = toolIdFromPart(part)
  let label = "Herramienta"
  if (toolId) {
    label = getAgentToolDisplayLabel(toolId)
  } else if ("title" in part && typeof part.title === "string" && part.title.trim()) {
    label = part.title.trim()
  }
  const state = "state" in part ? part.state : undefined
  const awaitingUserApproval = state === "approval-requested"
  const awaitingAskQuestionsInput =
    toolId === "ask_questions" && state === "input-available"
  const active = typeof state === "string" && toolActiveStates(state)
  return {
    id,
    kind: STREAM_ACTIVITY_KIND_TOOL,
    status: active ? "active" : "completed",
    label,
    ...(toolId ? { toolId } : {}),
    ...(awaitingUserApproval ? { awaitingUserApproval: true } : {}),
    ...(awaitingAskQuestionsInput ? { awaitingAskQuestionsInput: true } : {}),
  }
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @returns {boolean}
 */
function isToolLikePart(part) {
  if (part.type === "dynamic-tool") return true
  return typeof part.type === "string" && part.type.startsWith("tool-")
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @returns {boolean}
 */
function isSetMemoryEmptySuccessPart(part) {
  if (!isToolLikePart(part)) return false
  if (toolIdFromPart(part) !== "set_memory") return false
  const state = "state" in part ? part.state : undefined
  if (state !== "output-available") return false
  const output = "output" in part ? part.output : undefined
  return isEmptyLikeSetMemoryOutput(output)
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @returns {string | null}
 */
export function askQuestionsUserBubbleTextFromPart(part) {
  if (!isToolUIPart(part)) return null
  if (toolIdFromPart(part) !== "ask_questions") return null
  if (part.state !== "output-available") return null
  const out = "output" in part ? part.output : undefined
  if (typeof out === "string" && out.length > 0) return out
  return null
}

/**
 * @param {import("ai").UIMessage["parts"][number][]} parts
 * @returns {import("./estimationAgent.types").AssistantRenderItem[]}
 */
export function assistantPartsToRenderItems(parts) {
  /** @type {import("./estimationAgent.types").AssistantRenderItem[]} */
  const items = []
  /** @type {import("./estimationAgent.types").StreamActivitySegment[]} */
  let activitySegments = []
  /** @type {import("ai").UIMessage["parts"][number][]} */
  let pendingAskBubbleParts = []
  let textBuffer = ""
  let partIndex = 0

  function flushText() {
    if (textBuffer) {
      items.push({
        type: "text",
        id: `txt-${items.length}`,
        text: textBuffer,
      })
      textBuffer = ""
    }
  }

  function flushActivities() {
    if (activitySegments.length) {
      items.push({
        type: "activities",
        id: `act-${items.length}`,
        segments: activitySegments.map((s) => ({ ...s })),
      })
      for (const toolPart of pendingAskBubbleParts) {
        const bubbleText = askQuestionsUserBubbleTextFromPart(toolPart)
        if (bubbleText == null) continue
        const tc =
          "toolCallId" in toolPart && typeof toolPart.toolCallId === "string" ?
            toolPart.toolCallId
          : `ask-${items.length}`
        items.push({
          type: "ask-user-bubble",
          id: `ask-bub-${tc}`,
          text: bubbleText,
          toolCallId: tc,
        })
      }
      activitySegments = []
      pendingAskBubbleParts = []
    }
  }

  for (const part of parts) {
    if (part.type === "text") {
      flushActivities()
      textBuffer += part.text
    } else if (part.type === "reasoning") {
      flushText()
      activitySegments.push(reasoningPartToSegment(part, partIndex++))
    } else if (isToolLikePart(part)) {
      flushText()
      activitySegments.push(toolPartToSegment(part, partIndex++))
      if (askQuestionsUserBubbleTextFromPart(part)) {
        pendingAskBubbleParts.push(part)
      }
      if (isSetMemoryEmptySuccessPart(part)) {
        flushActivities()
        items.push({
          type: "text",
          id: `set-memory-empty-${items.length}`,
          text: SET_MEMORY_EMPTY_OUTPUT_NOTICE,
        })
      }
    } else if (part.type === "step-start") {
      continue
    } else {
      flushText()
    }
  }
  flushText()
  flushActivities()
  return items
}

/**
 * @param {import("ai").UIMessage} message
 * @returns {boolean}
 */
export function messageHasPendingAskQuestionsInput(message) {
  if (message.role !== "assistant") return false
  for (const p of message.parts ?? []) {
    if (!isToolUIPart(p)) continue
    if (toolIdFromPart(p) !== "ask_questions") continue
    if (p.state === "input-available") return true
  }
  return false
}

/**
 * @param {import("ai").UIMessage[]} messages
 * @returns {boolean}
 */
export function hasPendingAskQuestionsInput(messages) {
  return messages.some(messageHasPendingAskQuestionsInput)
}
